import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { styled } from "@mui/material/styles";
import { black, grey } from "designSystem/styles/color";
import FlexTopDrawer from "designSystem/components/flex/FlexTopDrawer";
import { CSS_ANCHOR_RESET } from "designSystem/styles/style-functions";
import OverflowText from "designSystem/components/text/OverflowText";
import SearchTextField from "designSystem/components/textfield/SearchTextField";
import { ACTIVE_NOT_SELECTED, HOVER, SELECTED } from "designSystem/theme";
import locale from "csi/locale";
import { DataSetupSection, useDataSetupStore } from "csi/state/datasetup";
import { useStore } from "csi/state/hooks";
import { when } from "mobx";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useRef } from "react";
import DataSetupSearchContent from "./DataSetupSearchContent";
import DataSetupSectionContent from "./DataSetupSectionContent";
import SectionSwitcher from "./SectionSwitcher";

interface SectionListProps {
	activeSection: DataSetupSection;
	sections: DataSetupSection[];
};

const LISTITEM = "csi-datasetup-listitem";

const StyledList = styled(List)((
	{
		theme,
	},
) => ({
	[`& .${LISTITEM}`]: {
		color: black[500],

		[HOVER]: {
			color: black[100],
		},
		[ACTIVE_NOT_SELECTED]: {
			backgroundColor: theme.palette.HighlightSelected,
		},
		[SELECTED]: {
			backgroundColor: grey[200],
			color: black[100],
		},
	},
}));

const SectionList: FunctionComponent<SectionListProps> = (props) => {
	const { activeSection, sections } = props;

	return (
		<StyledList>
			{sections.map((section) => (
				<ListItem
					className={`${CSS_ANCHOR_RESET} ${LISTITEM}`}
					component="a"
					href={section.href}
					key={section.id}
					selected={section === activeSection}
				>
					<OverflowText
						bold
						text={section.name}
						tooltipProps={{ placement: "right" }}
					/>
				</ListItem>
			),
			)}
		</StyledList>
	);
};

export const DataSetupNavigator = observer(() => {
	const dataSetupStore = useDataSetupStore();
	const { browserStore, localStore } = useStore();
	const { activeSection, activeSections, searchFocused, searchText } = dataSetupStore;
	const { sectionSwitcherOpen } = localStore.dataSetup;
	const searchPlaceholder = locale.resource("DataSetupSearch");
	const showSearchField = (activeSection !== dataSetupStore.serviceSection);
	const escapeRef = useRef();

	const n = activeSections.length;
	const disableSplitter = n === 1;
	const padding = 16;
	const componentHeight = 26;
	const sectionHeight = 32;
	const searchHeight = showSearchField ? (componentHeight + (searchFocused ? 0 : padding)) : 0;
	const baseHeight = padding * 2 + searchHeight;
	const heightClosed = baseHeight + (searchFocused ? 0 : componentHeight);
	const heightOpen = baseHeight + (searchFocused ? 0 : (sectionHeight * n));

	function unfocus() {
		// focus on section list
		const domNode: HTMLElement = escapeRef.current;
		domNode.focus();
	}

	async function onBlur() {
		// onBlur will be invoked on the mousedown event, so if user holds down the mouse over a search result
		// then the search will be stopped and the navigation to the search result will not work. To fix this,
		// wait for the mouse to be up
		await when(() => !browserStore.isMouseDown);

		// delay stopping search so clicking on anchor fires navigation event; otherwise, the click seems
		// to be ignored since stopping the search removes search results. This is needed even after waiting
		// for mouse to be up
		setTimeout(() => dataSetupStore.stopSearch(), 100);
	}

	function onEnter() {
		unfocus();
		dataSetupStore.selectSearchItem();
	}

	function onEscape() {
		unfocus();
		dataSetupStore.stopSearch();
	}

	const topChildren = (closed: boolean) => {
		const showList = n === 1 || !closed;

		return (
			<Box py={4}>
				<div ref={escapeRef} tabIndex={-1} />
				{showSearchField && (
					<Box px={4}>
						<SearchTextField
							autoFocus={searchFocused}
							fullWidth
							onBlur={onBlur}
							onChange={(event) => dataSetupStore.setSearchText(event.target.value)}
							onDown={() => dataSetupStore.selectNextSearchResult(false)}
							onEnter={onEnter}
							onEscape={onEscape}
							onFocus={() => dataSetupStore.startSearch()}
							onUp={() => dataSetupStore.selectNextSearchResult(true)}
							placeholder={searchPlaceholder}
							value={searchText}
						/>
					</Box>
				)}
				{!searchFocused && (
					<Box
						pt={showSearchField ? 4 : 0}
						px={showList ? 0 : 4}
					>
						{showList ?
							<SectionList activeSection={activeSection} sections={activeSections} />
							:
							<SectionSwitcher activeSection={activeSection} sections={activeSections} />
						}
					</Box>
				)}
			</Box>
		);
	};

	return (
		<FlexTopDrawer
			component="nav"
			disabled={disableSplitter}
			heightClosed={heightClosed}
			heightOpen={heightOpen}
			hideSplitter={searchFocused}
			onToggleOpen={localStore.dataSetup.setSectionSwitcherOpen}
			open={sectionSwitcherOpen || disableSplitter}
		>
			{{
				top: topChildren(false),
				topWhenClosed: topChildren(true),
				bottom:
					searchFocused ?
						<DataSetupSearchContent />
						:
						<DataSetupSectionContent />,

			}}
		</FlexTopDrawer>
	);
});

export default DataSetupNavigator;
